import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useBackBarContext } from './BackBarContext';
import { isTopLevelRoute, resolveParent, routeLabel } from './parentRegistry';
import * as styles from './BackBar.css.ts';

type BackBarLocationState = {
  backBar?: {
    to: string;
    label: string;
  };
};

export function BackBar(): React.ReactElement | null {
  const location = useLocation();
  const { override, from } = useBackBarContext();

  const stateTarget = (location.state as BackBarLocationState | null)?.backBar ?? null;
  const fallback = resolveParent(location.pathname);

  // Top-level pages don't get an automatic origin bar, but explicit overrides
  // (e.g. project-scoped receipts list) and link-provided targets still show.
  const isTopLevel = isTopLevelRoute(location.pathname);
  const currentPath = location.pathname + location.search;
  const fromTarget =
    !isTopLevel && from && from.to !== currentPath
      ? { to: from.to, label: from.label ?? routeLabel(from.to) }
      : null;

  // Resolution order: tracked origin first — "back" means where you came
  // from, known synchronously at navigation time. Overrides, link-provided
  // state, and the registry only fill in when the trail is empty (deep link,
  // hard refresh).
  const target = fromTarget ?? override ?? stateTarget ?? fallback;
  if (!target) return null;

  // Always navigate to the target path directly. Never navigate(-1): once the
  // bar has been used to go "up", real browser history diverges from the
  // breadcrumb trail, so popping history can land on the page just left.
  // The provider's unwind-on-revisit rule keeps the trail loop-free.
  return (
    <div className={styles.bar}>
      {/* backBarNav tells the provider this navigation goes "up" the trail,
          so the page being left must not be recorded as the new origin. */}
      <Link to={target.to} state={{ backBarNav: true }} className={styles.link}>
        <span className={styles.chevron} aria-hidden="true">
          ←
        </span>
        <span>{target.label ? `Back to ${target.label}` : 'Back'}</span>
      </Link>
    </div>
  );
}

export default BackBar;
